import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n, intlTag } from "@/i18n";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";

export default function InventurReport() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, fmtMoney, locale } = useI18n();
  const [rep, setRep] = useState(null);
  const dfmt = (s) => { try { return new Date(s).toLocaleString(intlTag(locale)); } catch { return s; } };

  useEffect(() => { api.get(`/inventory-counts/${id}/report`).then((r) => setRep(r.data)).catch(() => {}); }, [id]);
  if (!rep) return <div className="text-muted-foreground p-8">{t("common.loading")}</div>;
  const c = rep.count;
  const s = rep.sales;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 print:hidden">
        <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="report-back"><ArrowLeft className="h-4 w-4" /> {t("common.back")}</button>
        <Button onClick={() => window.print()} data-testid="report-print"><Printer className="h-4 w-4 mr-1.5" /> {t("common.printPdf")}</Button>
      </div>

      <div className="mx-auto max-w-3xl rounded-md border border-border bg-white text-black p-10 shadow-sm" data-testid="inventur-report-doc">
        <div className="flex justify-between items-start">
          <div>
            <div className="font-heading text-2xl font-bold">{t("cntr.title")}</div>
            <div className="text-xs text-neutral-600">Gluty-Free Club · Consberry GmbH</div>
          </div>
          <div className="text-right text-xs text-neutral-600">
            <div className={`font-semibold ${c.status === "final" ? "text-green-700" : "text-amber-600"}`}>{c.status === "final" ? t("cnt.finalLocked") : t("cnt.draft")}</div>
            <div>{t("cntr.countedAt")}: {dfmt(c.created_at)}</div>
            <div>{t("cntr.countedBy")}: {c.created_by_email}</div>
            {c.finalized_at && <div>{t("cnt.finalizedAt")}: {dfmt(c.finalized_at)} · {c.finalized_by}</div>}
          </div>
        </div>

        <h3 className="mt-6 mb-2 font-heading text-lg font-semibold">{t("cntr.sollIst")}</h3>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-neutral-300 text-left"><th className="py-1">SKU</th><th>{t("cnt.item")}</th><th className="text-right">{t("cntr.expected")}</th><th className="text-right">{t("cntr.actual")}</th><th className="text-right">{t("cnt.difference")}</th></tr></thead>
          <tbody data-testid="inventur-report-lines">
            {rep.lines.map((l, i) => (
              <tr key={i} className="border-b border-neutral-100">
                <td className="py-1 font-mono">{l.sku}</td><td>{l.product_name}</td>
                <td className="text-right font-mono whitespace-nowrap">{l.system_qty}</td><td className="text-right font-mono whitespace-nowrap">{l.counted_qty}</td>
                <td className={`text-right font-mono ${l.difference < 0 ? "text-red-600" : l.difference > 0 ? "text-green-700" : ""}`}>{l.difference > 0 ? "+" : ""}{l.difference}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 className="mt-8 mb-2 font-heading text-lg font-semibold">{t("cntr.salesTitle")}</h3>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-neutral-300 text-left"><th className="py-1">{t("cntr.segment")}</th><th className="text-right">{t("common.revenue")}</th><th className="text-right">{t("common.cogs")}</th><th className="text-right">{t("cntr.contribution")}</th></tr></thead>
          <tbody data-testid="inventur-report-sales">
            <tr className="border-b border-neutral-100"><td className="py-1">B2B</td><td className="text-right font-mono whitespace-nowrap">{fmtMoney(s.b2b.revenue)}</td><td className="text-right font-mono whitespace-nowrap">{fmtMoney(s.b2b.cogs)}</td><td className="text-right font-mono whitespace-nowrap">{fmtMoney(s.b2b.contribution_margin)}</td></tr>
            <tr className="border-b border-neutral-100"><td className="py-1">B2C</td><td className="text-right font-mono whitespace-nowrap">{fmtMoney(s.b2c.revenue)}</td><td className="text-right font-mono whitespace-nowrap">{fmtMoney(s.b2c.cogs)}</td><td className="text-right font-mono whitespace-nowrap">{fmtMoney(s.b2c.contribution_margin)}</td></tr>
          </tbody>
        </table>
        <div className="mt-3 text-xs text-neutral-500">{t("cntr.channelsDetail")}: {s.by_channel.map((ch) => `${ch.channel} (${fmtMoney(ch.revenue)})`).join(" · ") || "—"}</div>
      </div>
    </div>
  );
}
